import { ReactElement, useState } from 'react'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { useUIStore } from '@stores'
import { getProject } from '@queries'
import { editProject } from '@mutations'
import ProjectDataDialog from 'src/components/pages/Home/components/ProjectDataDialog'
import { usePersistentParams } from 'src/hooks'

/**
 * Mounted once in the editor tree (not inside the navbar's "Bearbeiten" menu
 * item) so it survives the navbar dropdown closing -- see the isEditProjectDialogOpen
 * comment in the ui store.
 */
const EditProjectDialog = (): ReactElement | null => {
  const { projectId }: { projectId?: string } = usePersistentParams()
  const isOpen = useUIStore(state => state.isEditProjectDialogOpen)
  const setIsOpen = useUIStore(state => state.setIsEditProjectDialogOpen)
  const [invalidError, setInvalidError] = useState<string | null>(null)
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  // Same query key the editor's own data loading already uses, so this reads
  // the cached project instead of firing a second request.
  const { data: project } = useQuery(getProject.getKey(projectId), () =>
    getProject.request(projectId),
  )

  const { mutate: editMutate, isLoading } = useMutation(
    (data: ProjectData) => editProject.request(projectId as string, data),
    {
      onSuccess: async () => {
        // Both the editor's own project (this dialog's data) and the Home
        // page's project list (edited elsewhere) need the fresh values.
        await Promise.all([
          queryClient.invalidateQueries(getProject.getKey(projectId)),
          queryClient.invalidateQueries(['getProjects']),
        ])
        enqueueSnackbar('Projekt erfolgreich bearbeitet', { variant: 'success' })
        setIsOpen(false)
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Bearbeiten des Projektes'), {
          variant: 'error',
        })
      },
    },
  )

  // The editor's own query response covers every field the form needs -- it
  // is typed narrower (ProjectWithLoadInfo) than ProjectData because that
  // type was written for the editor's own, smaller needs.
  const projectData = project as unknown as ProjectData | undefined

  if (!isOpen || !projectData) return null

  return (
    <ProjectDataDialog
      open
      onClose={() => setIsOpen(false)}
      onSubmit={values => editMutate({ ...projectData, ...values })}
      isLoading={isLoading}
      invalidError={invalidError}
      setInvalidError={setInvalidError}
      project={projectData}
    />
  )
}

export default EditProjectDialog
